import Cookies from 'js-cookie';
import { clarity } from 'react-microsoft-clarity';

import { CLARITY_PROHIBITED_ROUTES } from '@/constants/clarity';
import { hasWindow } from './dom';
import { matchesRouteSegment, normalizePathname } from './navigation';

type ClarityState = {
	initialized: boolean;
	lastRouteDecision?: boolean;
	lastConsent?: boolean;
	consentPollId?: number;
	consentUnsubscribe?: () => void;
};

let state: ClarityState = { initialized: false };

const NORMALIZED_PROHIBITED_ROUTES = CLARITY_PROHIBITED_ROUTES.map(normalizePathname).filter(
	(route) => route.length > 0
);

function isProd(): boolean {
	return process.env.NEXT_PUBLIC_TARGET_ENV === 'prod';
}

function log(method: string, payload?: unknown): void {
	if (!isProd()) {
		// eslint-disable-next-line no-console
		console.log('[Clarity]', method, payload ?? '');
	}
}

function safeCall(call: () => void): void {
	try {
		call();
	} catch {
		// Clarity must never break the page.
	}
}

export function isClarityTrackedPath(pathname: string): boolean {
	const path = normalizePathname(pathname);
	return !NORMALIZED_PROHIBITED_ROUTES.some((route) => matchesRouteSegment(path, route));
}

// The cookie bar SDK (loaded from /sdk/cookie/*.js) owns the `mp_ck_set` consent cookie
// ('A=1&P=0&R=0') and, when available, exposes its consent API on `window.skippay.cookie`.
const CONSENT_COOKIE_NAME = 'mp_ck_set';

export function hasAnalyticsCookieConsent(): boolean {
	return /(^|&)A=1(&|$)/.test(Cookies.get(CONSENT_COOKIE_NAME) || '');
}

function applyConsent(granted: boolean): void {
	if (state.lastConsent === granted) {
		return;
	}
	state.lastConsent = granted;
	log('consent', granted);
	safeCall(() => clarity.consent(granted));
}

const CONSENT_POLL_INTERVAL_MS = 400;
const CONSENT_POLL_MAX_ATTEMPTS = 25;

function wireConsent(): void {
	state.lastConsent = false;
	if (hasAnalyticsCookieConsent()) {
		applyConsent(true);
	}

	let attempts = 0;
	state.consentPollId = window.setInterval(() => {
		const cookieSdk = window.skippay?.cookie;
		if (cookieSdk?.onConsentsChange) {
			window.clearInterval(state.consentPollId);
			applyConsent(Boolean(cookieSdk.getConsents?.()?.analytics));
			state.consentUnsubscribe = cookieSdk.onConsentsChange((consents) =>
				applyConsent(Boolean(consents?.analytics))
			);
			return;
		}
		attempts += 1;
		if (attempts >= CONSENT_POLL_MAX_ATTEMPTS) {
			window.clearInterval(state.consentPollId);
			return;
		}
		applyConsent(hasAnalyticsCookieConsent());
	}, CONSENT_POLL_INTERVAL_MS);
}

export function clarityEvaluateRoute(pathname: string): void {
	if (!hasWindow() || !state.initialized || !isProd()) {
		return;
	}

	const shouldTrack = isClarityTrackedPath(pathname);

	if (shouldTrack === state.lastRouteDecision) {
		return;
	}

	state.lastRouteDecision = shouldTrack;
	log(shouldTrack ? 'start' : 'stop', pathname);

	safeCall(() => (shouldTrack ? clarity.start() : clarity.stop()));
}

export function initClarity(): void {
	if (!hasWindow() || state.initialized) {
		return;
	}
	if (process.env.NODE_ENV === 'development') {
		return;
	}

	const projectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;

	if (!projectId) {
		log('skip', { reason: 'no project id' });
		return;
	}

	log('init', projectId);
	safeCall(() => clarity.init(projectId));

	if (!clarity.hasStarted()) {
		log('skip', { reason: 'clarity did not start' });
		return;
	}

	state.initialized = true;

	clarityEvaluateRoute(window.location.pathname);
	wireConsent();
}

export function __resetClarityForTests(): void {
	if (hasWindow() && state.consentPollId !== undefined) {
		window.clearInterval(state.consentPollId);
	}
	state.consentUnsubscribe?.();
	state = { initialized: false };
}
